import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { partnerSectionLinks } from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { useLocalizedCountriesOrRegionsList } from "../../helpers/countriesOrRegionsList";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import {
  DevelopmentHardwareStoreStatus,
  DeviceOrderingAddressesResponse,
  DeviceOrderingCountry,
  SaveShippingAddressRequest,
} from "../../models/developmentHardware";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import * as styles from "./PartnerShippingAddressPage.module.css";

const addressesUrl = "/games/partner/development-hardware-addresses";
const getShippingAddressUrl = (billingAccountId: string, shippingAddressId: string) =>
  `/games/partner/development-hardware-billing-accounts/${billingAccountId}/shipping-addresses/${shippingAddressId}`;

const emptyForm: SaveShippingAddressRequest = {
  addressName: "",
  line1: "",
  line2: "",
  city: "",
  stateOrProvince: "",
  postalCode: "",
  phone: "",
  countryId: "",
};

export const PartnerShippingAddressPage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaSigned);
  const isSignedIn = useRequireAuth();
  const data = useSelector(getXboxOnboardData);
  const history = useHistory();
  const { billingAccountId, shippingAddressId } = useParams<{
    billingAccountId: string;
    shippingAddressId: string;
  }>();
  const [
    pageLabel,
    shippingAddressTitle,
    addressNameLabel,
    addressLine1Label,
    addressLine2Label,
    cityLabel,
    stateLabel,
    postalCodeLabel,
    phoneLabel,
    countryLabel,
    selectLabel,
    cancelLabel,
    submitLabel,
    requiredFieldsError,
    submitError,
  ] = useStrings(
    "deviceOrderingAddressesLabel",
    "shippingAddress",
    "developmentHardwareShippingAddressName",
    "xboxOnboardAddressLine1",
    "addressLine2",
    "city",
    "stateRegion",
    "postalCode",
    "xboxOnboardPhone",
    "countryOrRegion",
    "select",
    "cancelButton",
    "xboxOnboardSubmitButton",
    "requiredField",
    "saveSubmitFormErrorMessage"
  );
  const [form, setForm] = useState<SaveShippingAddressRequest>(emptyForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [countryOptions, setCountryOptions] = useState<ReadonlyArray<DeviceOrderingCountry>>();
  const countries = useLocalizedCountriesOrRegionsList(countryOptions, false);

  useEffect(() => {
    if (!isPageReady || !isSignedIn || !data?.hasDevelopmentHardwareAccess || !data.isHubAdmin) {
      return;
    }

    let active = true;
    fetch(addressesUrl)
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load shipping address: ${response.status}`);
        }
        return (await response.json()) as DeviceOrderingAddressesResponse;
      })
      .then(response => {
        if (!active) {
          return;
        }
        const billingAccount = response.billingAccounts.find(
          account => account.billingAccountId === billingAccountId
        );
        const addressInAnotherAccount = response.billingAccounts.some(
          account =>
            account.billingAccountId !== billingAccountId &&
            account.shippingAddresses.some(address => address.addressId === shippingAddressId)
        );
        if (!billingAccount || addressInAnotherAccount) {
          setLoadFailed(true);
          setIsLoading(false);
          return;
        }
        const shippingAddress = billingAccount.shippingAddresses.find(
          address => address.addressId === shippingAddressId
        );
        setCountryOptions(
          shippingAddress?.countryId &&
            !billingAccount.shippingCountriesOrRegions.some(
              country => country.id === shippingAddress.countryId
            )
            ? [
                ...billingAccount.shippingCountriesOrRegions,
                { id: shippingAddress.countryId, value: shippingAddress.countryName },
              ]
            : billingAccount.shippingCountriesOrRegions
        );
        if (shippingAddress) {
          setForm({
            addressName: shippingAddress.name,
            countryId: shippingAddress.countryId ?? "",
            line1: shippingAddress.line1,
            line2: shippingAddress.line2 ?? "",
            city: shippingAddress.city,
            stateOrProvince: shippingAddress.stateOrProvince,
            postalCode: shippingAddress.postalCode,
            phone: shippingAddress.phone ?? "",
          });
        }
        setIsReadOnly(
          billingAccount.storeStatus !== DevelopmentHardwareStoreStatus.Open ||
            shippingAddress?.isPrimaryBillingAddress === true ||
            shippingAddress?.isActive === false
        );
        setIsLoading(false);
      })
      .catch(() => {
        if (active) {
          setLoadFailed(true);
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    billingAccountId,
    data?.hasDevelopmentHardwareAccess,
    data?.isHubAdmin,
    isPageReady,
    isSignedIn,
    shippingAddressId,
  ]);

  const update = (field: keyof SaveShippingAddressRequest, value: string) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      [
        form.addressName,
        form.line1,
        form.city,
        form.stateOrProvince,
        form.postalCode,
        form.phone,
        form.countryId,
      ].some(value => !value.trim())
    ) {
      setError(requiredFieldsError);
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      const response = await fetch(getShippingAddressUrl(billingAccountId, shippingAddressId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        throw new Error(`Failed to save shipping address: ${response.status}`);
      }
      history.push(partnerSectionLinks.deviceOrderingAddresses.to);
    } catch {
      setError(submitError);
      setIsSubmitting(false);
    }
  };

  if (!isPageReady || !isSignedIn || !data || isLoading) {
    return (
      <PartnerPageLayout breadcrumbLabel={shippingAddressTitle}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }
  if (!data.isHubAdmin || !data.hasDevelopmentHardwareAccess) {
    return null;
  }
  if (loadFailed) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={shippingAddressTitle}
        breadcrumbSteps={[{ label: pageLabel, to: partnerSectionLinks.deviceOrderingAddresses.to }]}
      >
        <p role="alert">{submitError}</p>
      </PartnerPageLayout>
    );
  }

  const fields: ReadonlyArray<
    Readonly<{
      field: keyof SaveShippingAddressRequest;
      label: string;
      required: boolean;
      type?: string;
    }>
  > = [
    { field: "line1", label: addressLine1Label, required: true },
    { field: "line2", label: addressLine2Label, required: false },
    { field: "city", label: cityLabel, required: true },
    { field: "stateOrProvince", label: stateLabel, required: true },
    { field: "postalCode", label: postalCodeLabel, required: true },
    { field: "phone", label: phoneLabel, required: true, type: "tel" },
  ];

  return (
    <PartnerPageLayout
      breadcrumbLabel={shippingAddressTitle}
      breadcrumbSteps={[{ label: pageLabel, to: partnerSectionLinks.deviceOrderingAddresses.to }]}
    >
      <form className={styles.form} aria-busy={isSubmitting} onSubmit={submit}>
        <header className={styles.pageHeader}>
          <h1>{shippingAddressTitle}</h1>
          <div className={styles.actions}>
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              type="button"
              disabled={isSubmitting}
              onClick={() => history.push(partnerSectionLinks.deviceOrderingAddresses.to)}
            >
              {cancelLabel}
            </BasicButton>
            {!isReadOnly && (
              <BasicButton
                as="button"
                buttonType={ButtonType.Brand}
                type="submit"
                disabled={isSubmitting || !form.countryId}
              >
                {submitLabel}
              </BasicButton>
            )}
          </div>
        </header>

        <div className={styles.formCard}>
          <div className={styles.accountDetailsSection}>
            <div className={styles.field}>
              <label htmlFor="shipping-address-name">
                {addressNameLabel}
                <span className={styles.requiredMark} aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="shipping-address-name"
                required
                aria-required="true"
                disabled={isReadOnly}
                value={form.addressName}
                onChange={event => update("addressName", event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="shipping-address-country">
                {countryLabel}
                <span className={styles.requiredMark} aria-hidden="true">
                  *
                </span>
              </label>
              <select
                id="shipping-address-country"
                required
                aria-required="true"
                disabled={isReadOnly}
                value={form.countryId}
                onChange={event => update("countryId", event.target.value)}
              >
                <option value="">{selectLabel}</option>
                {countries.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.detailsGrid}>
            {fields.map(({ field, label, required, type }) => (
              <div className={styles.field} key={field}>
                <label htmlFor={`shipping-address-${field}`}>
                  {label}
                  {required && (
                    <span className={styles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  )}
                </label>
                <input
                  id={`shipping-address-${field}`}
                  type={type ?? "text"}
                  required={required}
                  aria-required={required || undefined}
                  disabled={isReadOnly}
                  value={form[field]}
                  onChange={event => update(field, event.target.value)}
                />
              </div>
            ))}
          </div>

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </div>
      </form>
    </PartnerPageLayout>
  );
};
